import { zodResolver } from '@hookform/resolvers/zod';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { Controller, SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { z } from 'zod';
import { KTSVG } from '../../../../../../_metronic/helpers';
import { useToast } from '../../../../../modules/components/toast/ToastContext';
import {
  CategorizeServiceSection,
  FieldStatus,
  keywordLabel,
  KeywordType,
  sexualCategoryLabel,
  SiService,
  siteIntelligenceServiceLabel,
} from '../../../mappings';
import { UsePageDataManagerResult } from '../hooks/usePageDataManager';
import { schema } from './Schema';

interface Props {
  data: {
    [SiService.ADULT_SHIELD_SERVICE]: any;
    [SiService.TOBACCO_SIFT_SERVICE]: any;
    [SiService.MEMBERS_ONLY_SIFT_SERVICE]: any;
    [SiService.CATEGORIZE_SERVICE]: any;
  };
  onClose: () => void;
  onUpdate: UsePageDataManagerResult['updateField'];
  onSaveMultiple: UsePageDataManagerResult['saveMultipleServices'];
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  defaultScreenshotUrl?: string;
}

const SiAiAddCategory = ({
  data,
  onClose,
  onUpdate,
  onSaveMultiple,
  isLoading,
  setIsLoading,
}: Props) => {
  const intl = useIntl();
  const { setToast } = useToast();
  const [validateSchema, setValidateSchema] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const defaultSite = data?.[SiService.CATEGORIZE_SERVICE]?.query?.value
  const { categorySchema, siAiSexualCategoriesSchema, siAiTobaccoSchema, siAiCategorizeSchema } = schema(intl);

  const defaultSexualCategories: z.infer<typeof siAiSexualCategoriesSchema> = {
    url: defaultSite ?? '',
    sexualCategories: [
      {
        name: '',
        screenshot: null,
      },
    ],
  };
  const defaultTobacco: z.infer<typeof siAiTobaccoSchema> = {
    url: defaultSite ?? '',
    screenshot: null,
    brands: [],
  };
  const defaultCategorize: z.infer<typeof siAiCategorizeSchema> = {
    url: defaultSite ?? '',
    screenshot: null,
    keywords: [],
  };
  const defaultValues = {
    category: '',
    ...defaultSexualCategories,
    ...defaultTobacco,
    ...defaultCategorize,
  };
  const {
    handleSubmit,
    control,
    watch,
    resetField,
    reset,
  } = useForm({
    resolver: zodResolver(validateSchema),
    defaultValues,
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'sexualCategories',
  });

  const selectedCategory = watch('category');
  const isKeywordCategory = KeywordType?.[selectedCategory] !== undefined;

  useEffect(() => {
    if (!selectedCategory || selectedCategory === SiService.MEMBERS_ONLY_SIFT_SERVICE) {
      resetField('url');
      setValidateSchema(categorySchema);
    } else if (selectedCategory === SiService.ADULT_SHIELD_SERVICE) {
      setValidateSchema(categorySchema.and(siAiSexualCategoriesSchema));
    } else if (selectedCategory === SiService.TOBACCO_SIFT_SERVICE) {
      setValidateSchema(categorySchema.and(siAiTobaccoSchema));
    } else if (isKeywordCategory) {
      resetField('keywords');
      setValidateSchema(categorySchema.and(siAiCategorizeSchema));
    }
  }, [selectedCategory]);

  const categoryOptions = [
    ...[
      SiService.ADULT_SHIELD_SERVICE,
      SiService.TOBACCO_SIFT_SERVICE,
      SiService.MEMBERS_ONLY_SIFT_SERVICE,
    ].map((key) => ({
      value: key,
      label: siteIntelligenceServiceLabel[key],
    })),
    ...Object.keys(KeywordType)
      .filter((key) => key !== KeywordType.ADULT)
      .map((key) => ({
        value: key,
        label: keywordLabel[key],
      })),
  ];

  const sexualCategoryOptions = Object.entries(sexualCategoryLabel).map(([key, label]) => ({
    value: key,
    label: intl.formatMessage({ id: label }),
  }));

  const cubanBrandOptions = [];

  const categorizeKeywordOptions = [];

  const handleClose = () => {
    reset();
    setErrorMessage('');
    onClose();
  };

  const onSubmit: SubmitHandler<typeof defaultValues> = async (formData) => {
    setIsLoading(true);
    setErrorMessage('');

    const service = SiService?.[formData?.category] ?? SiService.CATEGORIZE_SERVICE;

    try {
      // Parse and update draft data
      if (service === SiService.MEMBERS_ONLY_SIFT_SERVICE) {
        onUpdate(service, 'result.members_only', true);
      } else if (service === SiService.ADULT_SHIELD_SERVICE) {
        const defaultKeyword = [{
          keyword_original: '',
          keyword_en: '',
          language: '',
          field_status: FieldStatus.ACTIVE,
        }];

        onUpdate(service, (currentDraft) => {
          const existingUrlObj = currentDraft?.detail?.find((item) => item.url === formData.url);
          if (existingUrlObj) {
            formData.sexualCategories.forEach((sexualCategory) => {
              const categoryObj = existingUrlObj.categories?.find(
                (c) => c.category === sexualCategory.name
              );
              if (categoryObj) {
                categoryObj.capture_id = null;
                categoryObj.keywords = defaultKeyword;
              } else {
                existingUrlObj.categories?.push({
                  capture_id: null,
                  category: sexualCategory.name,
                  keywords: defaultKeyword,
                });
              }
            });
          } else {
            currentDraft?.detail?.push({
              capture_id: null,
              url: formData.url,
              categories: formData.sexualCategories.map((sexualCategory) => ({
                capture_id: null,
                category: sexualCategory.name,
                keywords: defaultKeyword,
              })),
            });
          }

          // Update sexual categories in result
          formData.sexualCategories.forEach((sexualCategory) => {
            const existingCategoryInResult = currentDraft?.result?.categories?.find(
              (c) => c.category === sexualCategory.name
            );
            if (existingCategoryInResult) {
              existingCategoryInResult.field_status = FieldStatus.ACTIVE;
            } else {
              currentDraft?.result?.categories?.push({
                category: sexualCategory.name,
                field_status: FieldStatus.ACTIVE,
              });
            }
          });
          // Ensure the adult toggle/result flag is set so the UI keeps the section visible
          if (!currentDraft.result) currentDraft.result = {};
          currentDraft.result.adult = true;
        });
      } else {
        if (service === SiService.TOBACCO_SIFT_SERVICE) {
          onUpdate(service, (currentDraft) => {
            const urlObj = currentDraft?.detail?.find((item) => item.url === formData.url);
            if (urlObj) {
              urlObj.capture_id = null;
              urlObj.tobacco_keywords = [];

              formData.brands?.forEach((brand) => {
                const existingBrandObj = urlObj.cuban_cigar_brands?.find(
                  (b) => b.brand === brand.name
                );
                if (existingBrandObj) {
                  existingBrandObj.field_status = FieldStatus.ACTIVE;
                } else {
                  urlObj.cuban_cigar_brands?.push({
                    brand: brand.name,
                    field_status: FieldStatus.ACTIVE,
                  });
                }
              });
            } else {
              currentDraft?.detail?.push({
                capture_id: null,
                url: formData.url,
                cuban_cigar_brands: formData.brands?.map((brand) => ({
                  brand: brand.name,
                  field_status: FieldStatus.ACTIVE,
                })),
                tobacco_keywords: [],
              });
            }

            // Update cuban cigar brands in result
            if (!currentDraft.result) currentDraft.result = {};
            currentDraft.result.tobacco = true;
            if (!currentDraft.result.cuban_cigar_brands) {
              currentDraft.result.cuban_cigar_brands = [];
            }

            formData.brands.forEach((brand) => {
              const existingCubanBrandInResult = currentDraft?.result?.cuban_cigar_brands?.find(
                (c) => c.brand === brand.name
              );
              if (existingCubanBrandInResult) {
                existingCubanBrandInResult.field_status = FieldStatus.ACTIVE;
              } else {
                currentDraft?.result?.cuban_cigar_brands?.push({
                  brand: brand.name,
                  field_status: FieldStatus.ACTIVE,
                });
              }
            });

            if ((currentDraft?.result?.cuban_cigar_brands?.length ?? 0) > 0) {
              currentDraft.result.cuban = true;
            }
          });
        } else if (service === SiService.CATEGORIZE_SERVICE) {
          onUpdate(service, (currentDraft) => {
            const urlObjFromFormData = {
              capture_id: null,
              url: formData.url,
              keywords: [],
            };
            const categoriesDetail = currentDraft?.detail?.find(
              (item) => item.section === CategorizeServiceSection.CATEGORY
            )?.section_detail;
            const existingCategoryObj = categoriesDetail?.find(
              (c) => c.category === formData.category
            );

            if (existingCategoryObj) {
              const urlObj = existingCategoryObj?.url_detail?.find((u) => u.url === formData.url);
              if (urlObj) {
                urlObj.capture_id = null;
                urlObj.keywords = [];
              } else {
                existingCategoryObj.url_detail?.push(urlObjFromFormData);
              }
            } else {
              categoriesDetail?.push({
                category: formData.category,
                url_detail: [urlObjFromFormData],
              });
            }

            // Update result
            const existingCategoryInResult = currentDraft?.result?.categories?.find(
              (c) => c.category === formData.category
            );
            if (existingCategoryInResult) {
              existingCategoryInResult.field_status = FieldStatus.ACTIVE;
            } else {
              currentDraft?.result?.categories?.push({
                category: formData.category,
                field_status: FieldStatus.ACTIVE,
              });
            }
          });
        }
      }

      // Save draft data
      await onSaveMultiple([
        {
          selector: service,
          ...(service === SiService.CATEGORIZE_SERVICE && {
            paths: [
              `detail[${data?.[SiService.CATEGORIZE_SERVICE]?.detail?.findIndex(
                (item) => item.section === CategorizeServiceSection.CATEGORY
              )}]`,
              'result.categories',
            ],
          }),
        },
      ]);

      handleClose();
    } catch (err) {
      setToast({
        show: true,
        type: 'Danger',
        header: intl.formatMessage({ id: 'MESSAGE.ERROR' }),
        message: 'Fail to add category.',
      });
      setErrorMessage(err?.message || intl.formatMessage({ id: 'MESSAGE.AN_ERROR_OCCURRED' }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className='d-flex flex-row-fluid flex-center bg-white rounded'>
      <form className='pt-5 w-100 w-xl-900px px-md-9' onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className='d-flex mb-4 align-items-center'>
          <label className='form-label required w-50'>Category</label>
          <Controller
            name='category'
            control={control}
            render={({ field, fieldState }) => {
              return (
                <Select
                  {...field}
                  placeholder={intl.formatMessage({ id: 'COMMON.SELECT' })}
                  className={`w-100 ${fieldState.invalid ? 'border rounded border-danger' : ''}`}
                  onChange={(e) => {
                    field.onChange(e?.value);
                  }}
                  options={categoryOptions}
                  value={categoryOptions.find((option) => option.value === field.value)}
                  isDisabled={isLoading}
                />
              );
            }}
          />
        </div>

        <div className='d-flex mb-4 align-items-center'>
          <label className='form-label required w-50'>
            URL <small>(must include http:// or https://)</small>
          </label>
          <Controller
            name='url'
            control={control}
            render={({ field, fieldState }) => {
              return (
                <input
                  {...field}
                  disabled={
                    isLoading ||
                    !selectedCategory ||
                    selectedCategory === SiService.MEMBERS_ONLY_SIFT_SERVICE
                  }
                  type='text'
                  className={clsx(
                    'form-control fw-normal fs-base',
                    fieldState.invalid && 'border border-danger'
                  )}
                  placeholder='https://example.com'
                />
              );
            }}
          />
        </div>

        {/* Adult-specific Fields */}
        {selectedCategory === SiService.ADULT_SHIELD_SERVICE && (
          <>
            <div className='separator my-6'></div>
            <div className='w-100'>
              {fields.map((field, index) => (
                // key={field.id} to ensure proper update on remove row
                <div className='d-inline-block w-100' key={field.id}>
                  <div className='d-flex justify-content-between align-items-start gap-4 mb-1'>
                    <div className='flex-grow-1'>
                      {index === 0 && (
                        <label className='form-label  required'>Sexual Category</label>
                      )}
                      <Controller
                        name={`sexualCategories.${index}.name`}
                        control={control}
                        render={({ field, fieldState }) => {
                          return (
                            <Select
                              {...field}
                              placeholder={intl.formatMessage({ id: 'COMMON.SELECT' })}
                              className={`w-100 ${fieldState.invalid ? 'border rounded border-danger' : ''
                                }`}
                              onChange={(e) => {
                                field.onChange(e?.value);
                              }}
                              options={sexualCategoryOptions}
                              value={sexualCategoryOptions.find(
                                (option) => option.value === field.value
                              )}
                              isDisabled={isLoading}
                            />
                          );
                        }}
                      />
                    </div>

                    <div className='d-flex align-items-center'>
                      <i
                        className={clsx(
                          'bi bi-dash-square-fill fs-1',
                          index > 0 ? 'text-danger cursor-pointer' : 'mt-8'
                        )}
                        onClick={() => {
                          if (index > 0) {
                            remove(index);
                          }
                        }}
                      ></i>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className='my-1'>
              <i
                className='bi bi-plus-square-fill fs-1 text-success cursor-pointer'
                onClick={() => {
                  append({
                    ...defaultValues.sexualCategories[0],
                    screenshot: null,
                  });
                }}
              ></i>
            </div>
          </>
        )}

        {/* Tobacco Brands */}
        {selectedCategory === SiService.TOBACCO_SIFT_SERVICE && (
          <div className='d-flex mb-4 align-items-center'>
            <label className='form-label w-50'>Cuban Cigar Brands</label>
            <Controller
              name='brands'
              control={control}
              render={({ field, fieldState }) => {
                return (
                  <CreatableSelect
                    {...field}
                    isMulti
                    placeholder={intl.formatMessage({ id: 'COMMON.SELECT' })}
                    noOptionsMessage={() => intl.formatMessage({ id: 'COMMON.SELECT' })}
                    className={`w-100 ${fieldState.invalid ? 'border rounded border-danger' : ''}`}
                    options={cubanBrandOptions}
                    value={field.value.map((value) => {
                      return { label: value.name, value: value.name, __isNew__: value.isNew };
                    })}
                    onChange={(selectedOptions) => {
                      field.onChange(
                        selectedOptions.map(
                          (option: { label: string; value: string; __isNew__: boolean }) => ({
                            name: option.value,
                            isNew: option.__isNew__,
                          })
                        )
                      );
                    }}
                    isDisabled={isLoading}
                  />
                );
              }}
            />
          </div>
        )}

        {/* Categorize Keywords */}
        {isKeywordCategory && (
          <div className='d-flex mb-4 align-items-center'>
            <label className='form-label w-50'>Keywords</label>
            <Controller
              name='keywords'
              control={control}
              render={({ field, fieldState }) => {
                return (
                  <CreatableSelect
                    {...field}
                    isMulti
                    placeholder={intl.formatMessage({ id: 'COMMON.SELECT' })}
                    noOptionsMessage={() => intl.formatMessage({ id: 'COMMON.SELECT' })}
                    className={`w-100 ${fieldState.invalid ? 'border rounded border-danger' : ''}`}
                    options={categorizeKeywordOptions}
                    value={field.value.map((value) => {
                      return { label: value.name, value: value.name, __isNew__: value.isNew };
                    })}
                    onChange={(selectedOptions) => {
                      field.onChange(
                        selectedOptions.map(
                          (option: { label: string; value: string; __isNew__: boolean }) => ({
                            name: option.value,
                            isNew: option.__isNew__,
                          })
                        )
                      );
                    }}
                    isDisabled={isLoading}
                  />
                );
              }}
            />
          </div>
        )}

        {errorMessage && (
          <div className='text-danger my-4 border border-danger rounded p-2'>{errorMessage}</div>
        )}

        <div className='d-flex justify-content-end mt-4'>
          <button
            className='btn btn-primary d-flex align-items-center gap-2'
            type='submit'
            disabled={isLoading}
          >
            {intl.formatMessage({ id: 'COMMON.SUBMIT' })}
            {isLoading ? (
              <span className='spinner-border spinner-border-sm align-middle'></span>
            ) : (
              <KTSVG path='/media/icons/duotune/arrows/arr064.svg' className='svg-icon-3 m-0' />
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export { SiAiAddCategory };
